import { useState } from 'react';
import { useLang } from 'hooks/lang';

import {
  actions,
  loaders,
  ClientSinglePackage,
  packageByStatus,
  calculateProjectTotal,
  isOwner,
  invoiceControlStatusConfig,
  isInvoiceRepriceActive,
  invoiceControlConfigByCode,
} from 'entities';

import { Alert, Card, Skeleton, Column, Row, Button } from 'components/Blocks';
import { PackageCheckIcon, RefreshIcon } from 'components/Icons';

import { PackageData } from './Data';
import { SelectPackage } from './SelectPackage';
import { QuotationRequests } from '../QuotationRequests';
import { QuotationPackageSelection } from './SelectPackageFromQuotation';
import { RemovePackageButton } from './RemovePackageButton';

import { useProjectProfile } from 'pages/Projects/ProjectProfile/context';
import { useUser } from 'hooks/user';

import { listGet } from 'utils/array';

export function Package() {
  const user = useUser();
  const { project, updateProject } = useProjectProfile();
  const { t } = useLang();

  const { invoice, services, id, currency } = project;

  const hasPackage = !!invoice.package;

  const [selectedRequest, setRequest] = useState('');

  const { data, loading, error, retry } = loaders.invoice.findPackage.use({
    defaultValue: {} as ClientSinglePackage,

    params: { id: invoice.package?.id as string },

    blockLoad: !hasPackage,

    reloadOnChangeRef: hasPackage,
  });

  const attackPackage = actions.invoice.attackProjectToPackage.use({
    onSuccess: (updatedPackage) => {
      updateProject((old) => ({
        ...old,

        invoice: {
          ...old.invoice,

          package: updatedPackage,

          status: invoiceControlStatusConfig.paid.code,
        },
      }));

      if (invoice.package?.id) retry();
    },
  });

  const attachPackageToProject = async (
    packageId: string,
    quotationId?: string,
  ): Promise<boolean> => {
    const updatedPackage = await attackPackage.execute({
      packageId,
      projectId: id,
      quotationId,
    });

    return !updatedPackage;
  };

  const isInvoiceAuthority =
    isOwner(invoice, user) || (user.team.isInvoiceTeam && user.type === 'LEADER');

  const canCheckValueMismatch = [hasPackage, data.id].every(Boolean);

  const packageProject = listGet(data.projects, id, ({ project }) => project.id);

  const valueMismatch = canCheckValueMismatch
    ? calculateProjectTotal(services) !==
      (packageProject?.valueInProjectCurrency ?? packageProject?.value)
    : false;

  const canSelectPackage = [
    !hasPackage,
    isOwner(invoice, user),
    !isInvoiceRepriceActive(project),
    invoiceControlConfigByCode[project.invoice.status].canSelectPackage,
  ].every(Boolean);

  if (!hasPackage && !canSelectPackage) return null;

  return (
    <Column>
      {(!hasPackage || valueMismatch) && (
        <>
          <QuotationPackageSelection
            attachPackageToProject={attachPackageToProject}
            requestId={selectedRequest}
            cancel={() => setRequest('')}
            loading={attackPackage.loading}
          />

          <QuotationRequests action={(request) => setRequest(request.id)} />
        </>
      )}

      <Card flow="column">
        {hasPackage && (
          <>
            {loading || error ? (
              <Skeleton height={60} width="100%" />
            ) : (
              <Column gap="6">
                <h6>{data.name}</h6>

                <PackageData data={data} />

                {valueMismatch && (
                  <Alert level="warning" title={t('project-value-mismatch')}>
                    {t('project-value-mismatch-explanation')}
                  </Alert>
                )}

                {isInvoiceAuthority && (
                  <Row>
                    <Button to="packageProfile" params={{ packageId: data.id }}>
                      <PackageCheckIcon />
                      {t('view')}
                    </Button>

                    {valueMismatch && currency === data.currency && (
                      <Button
                        onClick={() => attachPackageToProject(invoice.package?.id as string)}
                        loading={attackPackage.loading}
                        variant="ghost"
                      >
                        <RefreshIcon />
                        {t('re-attach-to-package')}
                      </Button>
                    )}

                    {packageByStatus[data.status]?.canRemoveProject && <RemovePackageButton />}
                  </Row>
                )}
              </Column>
            )}
          </>
        )}

        {canSelectPackage && (
          <SelectPackage
            attachPackageToProject={attachPackageToProject}
            loading={attackPackage.loading}
          />
        )}
      </Card>
    </Column>
  );
}
